import {FormEvent, useMemo, useState} from 'react';
import gql from 'graphql-tag';
import {useMutation} from 'services/apollo';
import {EzPage, EzCard, EzHeading, EzField, EzLink} from '@ezcater/recipe';
import {formatPhoneNumber} from 'util/StringUtils';
import Button from 'components/Button';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {usePageViewTracking} from 'util/tracking';
import {EXPIRED_MAGIC_LINK} from 'util/tracking/trackingEvents';
import {
  type DriverSelfAssignSource,
  type ExpiredMagicLinkCreateMutation,
  type ExpiredMagicLinkCreateMutationVariables,
} from 'services/graphql/types';
import {TERMS_URL} from 'paths';
import {getAdaptedSelfAssignSource} from 'util/urlUtils';
import Layout from '../Layout';

export const MagicLinkCreateMutation = gql(/* GraphQL */ `
  mutation ExpiredMagicLinkCreateMutation($input: DriverAccountMagicLinkCreateInput!) {
    magicLinkCreate: driverAccountMagicLinkCreate(input: $input) {
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

const getUrlParams = (
  search?: string
): {deliveryId?: string; driverAssignmentId?: string; source?: `${DriverSelfAssignSource}`} => {
  const urlParams = new URLSearchParams(search);

  return {
    deliveryId: urlParams.get('deliveryId') || undefined,
    driverAssignmentId: urlParams.get('driverAssignmentId') || undefined,
    source: getAdaptedSelfAssignSource(urlParams.get('source')),
  };
};

const ExpiredMagicLink = () => {
  usePageViewTracking(EXPIRED_MAGIC_LINK);
  const {search} = useLocation();
  const {t} = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');

  const {deliveryId, driverAssignmentId, source} = useMemo(() => getUrlParams(search), [search]);

  const [magicLinkRequestQuery, magicLinkRequestData] = useMutation<
    ExpiredMagicLinkCreateMutation,
    ExpiredMagicLinkCreateMutationVariables
  >(MagicLinkCreateMutation);

  const onChange = (value: string) => {
    setPhoneNumber(value);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await magicLinkRequestQuery({
        variables: {
          input: {
            phone: phoneNumber,
            assignmentId: driverAssignmentId,
            deliveryId,
            source,
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const defaultError = t('expiredMagicLink.error');

  const getError = () => {
    if (magicLinkRequestData.error) {
      return defaultError;
    }

    if (magicLinkRequestData?.data?.magicLinkCreate?.userErrors?.length) {
      return magicLinkRequestData?.data?.magicLinkCreate?.userErrors[0].message;
    }
  };

  const title = t('expiredMagicLink.title');
  const defaultHeader = t('expiredMagicLink.header');
  const defaultSubheader = t('expiredMagicLink.subheader');
  const phoneNumberText = t('expiredMagicLink.phoneNumber');
  const phoneNumberPlaceholder = t('expiredMagicLink.phoneNumberPlaceholder');
  const buttonText = t('expiredMagicLink.button');
  const disclaimer = t('expiredMagicLink.disclaimer');
  const terms = t('expiredMagicLink.terms');
  const successHeader = t('expiredMagicLink.successHeader');
  const successSubheader = t('expiredMagicLink.successSubheader');

  const error = getError();
  const showSuccessMessages = !!magicLinkRequestData.data?.magicLinkCreate && !error;

  const header = showSuccessMessages ? successHeader : defaultHeader;
  const subheader = showSuccessMessages ? successSubheader : defaultSubheader;

  return (
    <Layout title={title}>
      <EzPage>
        <EzHeading size={'3'}>{header}</EzHeading>
        <EzHeading size={'4'}>{subheader}</EzHeading>
        {!showSuccessMessages && (
          <form onSubmit={handleSubmit}>
            <EzCard>
              {error && <>{error}</>}
              {!showSuccessMessages && (
                <>
                  <EzField
                    type="text"
                    label={phoneNumberText}
                    value={formatPhoneNumber(phoneNumber)}
                    onChange={e => onChange(e.currentTarget.value)}
                    placeholder={phoneNumberPlaceholder}
                  />
                  <Button
                    type="submit"
                    buttonType="primary"
                    buttonText={buttonText}
                    loading={magicLinkRequestData.loading}
                    disabled={phoneNumber === ''}
                  />

                  <EzHeading size="6">
                    {disclaimer}&nbsp;&nbsp;
                    <EzLink href={TERMS_URL} target="_blank">
                      {terms}
                    </EzLink>
                  </EzHeading>
                </>
              )}
            </EzCard>
          </form>
        )}
      </EzPage>
    </Layout>
  );
};

export default ExpiredMagicLink;
